import { Link } from '@/lib/router-compat';
import { Button } from '@/components/ui/button';
import { ArrowLeftRight, BadgeCheck } from 'lucide-react';
import { useCountry } from '@/hooks/useCountry';
import { localizeFuel } from '@/utils/listingLocalization';
import { selectBestCardImage, hasValidPhoto } from '@/utils/imageSelection';
import { buildListingSlug } from '@/utils/listingSlug';
import type { ListingV2Response } from '@/types/listings-v2';

export function CompactMobileListingCard({ listing, onCompareToggle, isInComparison }: {
  listing: ListingV2Response;
  onCompareToggle?: (id: string) => void;
  isInComparison?: boolean;
}) {
  const { country } = useCountry();
  const photo = selectBestCardImage(listing.photos || [], null);
  const href = `/listing/${buildListingSlug(listing)}`;
  const fuel = localizeFuel(listing.attributes?.fuel_type || listing.attributes?.fuel, country === 'DE' ? 'de' : country === 'UK' ? 'en-GB' : 'ro');
  const details = [listing.year, listing.mileage_value != null ? `${listing.mileage_value.toLocaleString('ro-RO')} km` : null, fuel].filter(Boolean).join(' · ');

  return (
    <article className="grid h-[128px] grid-cols-[116px_minmax(0,1fr)] overflow-hidden rounded border border-border bg-card text-card-foreground" data-listing-id={listing.id}>
      <div className="relative min-h-0">
        <Link to={href} className="block h-full overflow-hidden bg-muted" aria-label={`${listing.make} ${listing.model}`}>
          {hasValidPhoto(photo) ? <img src={photo || ''} alt={`${listing.make} ${listing.model}`} loading="lazy" className="h-full w-full object-cover object-center" /> : <span className="flex h-full items-center justify-center text-xs text-muted-foreground">Fără fotografie</span>}
        </Link>
      </div>
      <div className="flex min-w-0 flex-col px-3 py-2.5">
        <Link to={href} className="block min-w-0">
          <h3 className="line-clamp-2 font-serif text-lg leading-[1.1] text-foreground">{listing.make} {listing.model} {listing.trim_level || ''}</h3>
        </Link>
        <p className="mt-1 font-serif text-[22px] leading-none text-brand-navy">{listing.price.toLocaleString('ro-RO')} {listing.currency === 'EUR' ? '€' : listing.currency}</p>
        <p className="mt-2 truncate text-[11px] text-muted-foreground" title={details}>{details}</p>
        <div className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1 border-t border-border pt-1">
          <span className="min-w-0 truncate text-xs text-muted-foreground">{listing.location_city || listing.location_country || ''}{listing.dealer_verified && <span className="ml-1.5 inline-flex items-center gap-0.5 font-medium text-brand-green"><BadgeCheck className="h-3 w-3" aria-hidden="true" />Verificat</span>}</span>
          {onCompareToggle && <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => onCompareToggle(listing.id)} aria-label={isInComparison ? 'Elimină din comparație' : 'Compară'} title={isInComparison ? 'Elimină din comparație' : 'Compară'} aria-pressed={isInComparison}><ArrowLeftRight className="h-4 w-4" /></Button>}
        </div>
      </div>
    </article>
  );
}